import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Dialog, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { extractApiErrorMessage } from '@/lib/api-client';

import { InvoicePdfPanel } from '../components/invoice-pdf-panel';
import { useCorrectInvoiceMutation } from '../api/use-correct-invoice-mutation';
import { InvoiceDialogShell } from './invoice-dialog-shell';
import type { InvoiceRow } from '../types';

const MIN_REASON_LENGTH = 5;

export interface CorrectInvoiceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly invoice: InvoiceRow;
  readonly onCorrected?: (() => void) | undefined;
}

// Mutation callbacks shared by submit — toast + close on success, translated
// toast on failure. Pulled out so the form hook stays within its line budget.
function correctionCallbacks(onCorrected: (() => void) | undefined, onClose: () => void) {
  return {
    onSuccess: () => {
      toast.success('Invoice number corrected.');
      onCorrected?.();
      onClose();
    },
    onError: (err: Error) => {
      toast.error(extractApiErrorMessage(err, 'Failed to correct the invoice.'));
    },
  };
}

// State + submission for the correction form. A submit is meaningful when the
// number changes or the attached PDF is replaced; the reason is required for
// the audit trail either way.
function useCorrectInvoiceForm(props: CorrectInvoiceDialogProps) {
  const { open, onOpenChange, invoice, onCorrected } = props;
  const [invoiceNumber, setInvoiceNumber] = useState(invoice.invoice_number ?? '');
  const [reason, setReason] = useState('');
  const [documentReplaced, setDocumentReplaced] = useState(false);

  useEffect(() => {
    if (open) {
      setInvoiceNumber(invoice.invoice_number ?? '');
      setReason('');
      setDocumentReplaced(false);
    }
  }, [open, invoice.invoice_number]);

  const mutation = useCorrectInvoiceMutation({
    invoiceId: invoice.id,
    orderId: invoice.order_id ?? '',
    blOrderId: invoice.bl_order_id ?? undefined,
  });

  const trimmedNumber = invoiceNumber.trim();
  const numberChanged = trimmedNumber.length > 0 && trimmedNumber !== invoice.invoice_number;
  const canSubmit =
    (numberChanged || documentReplaced) &&
    reason.trim().length >= MIN_REASON_LENGTH &&
    !mutation.isPending;

  const submit = () => {
    mutation.mutate(
      { invoice_number: trimmedNumber, reason: reason.trim(), document_replaced: documentReplaced },
      correctionCallbacks(onCorrected, () => {
        onOpenChange(false);
      }),
    );
  };

  return {
    invoiceNumber,
    setInvoiceNumber,
    reason,
    setReason,
    canSubmit,
    isPending: mutation.isPending,
    submit,
    markDocumentReplaced: () => {
      setDocumentReplaced(true);
    },
  };
}

type CorrectFormState = ReturnType<typeof useCorrectInvoiceForm>;

function CorrectFields({ form }: { readonly form: CorrectFormState }) {
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="correct-invoice-number">Invoice Number</Label>
        <Input
          id="correct-invoice-number"
          value={form.invoiceNumber}
          onChange={(e) => {
            form.setInvoiceNumber(e.target.value);
          }}
          disabled={form.isPending}
          maxLength={120}
          data-testid="correct-invoice-number"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="correct-invoice-reason">Reason</Label>
        <Textarea
          id="correct-invoice-reason"
          required
          minLength={MIN_REASON_LENGTH}
          maxLength={2000}
          value={form.reason}
          onChange={(e) => {
            form.setReason(e.target.value);
          }}
          disabled={form.isPending}
          rows={3}
          data-testid="correct-invoice-reason"
        />
        <p className="text-muted-foreground text-xs">
          Recorded for the audit trail. At least {MIN_REASON_LENGTH} characters.
        </p>
      </div>
    </>
  );
}

function CorrectForm({
  form,
  onDismiss,
}: {
  readonly form: CorrectFormState;
  readonly onDismiss: () => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (form.canSubmit) form.submit();
      }}
      className="flex flex-col gap-3"
    >
      <CorrectFields form={form} />
      <div className="mt-1 flex justify-end gap-2 border-t pt-3">
        <Button type="button" variant="outline" onClick={onDismiss} disabled={form.isPending}>
          Close
        </Button>
        <Button type="submit" disabled={!form.canSubmit} data-testid="correct-invoice-confirm">
          {form.isPending ? 'Saving…' : 'Save correction'}
        </Button>
      </div>
    </form>
  );
}

export function CorrectInvoiceDialog(props: CorrectInvoiceDialogProps) {
  const { open, onOpenChange, invoice } = props;
  const form = useCorrectInvoiceForm(props);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <InvoiceDialogShell
        singlePaneClassName="max-w-sm sm:max-w-lg"
        rightPane={
          <InvoicePdfPanel invoiceId={invoice.id} onDocumentChanged={form.markDocumentReplaced} />
        }
      >
        <DialogHeader>
          <DialogTitle>Correct invoice — Purchase Side</DialogTitle>
          <DialogDescription>
            Fix the invoice number or replace the uploaded document on an issued invoice. Amounts,
            currency and dates stay locked, and any recorded payment stays intact.
          </DialogDescription>
        </DialogHeader>
        <CorrectForm
          form={form}
          onDismiss={() => {
            onOpenChange(false);
          }}
        />
      </InvoiceDialogShell>
    </Dialog>
  );
}
